import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatInr, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
} from "recharts";
import { Download, Printer, BarChart3 } from "lucide-react";
import { CHART_COLORS, TX_TYPES, monthNameOf, todayIso } from "./shared";

const nowY = new Date().getFullYear();
const nowM = new Date().getMonth() + 1;

export default function ReportsPage() {
    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="reports-page">
            <header className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Reports & exports</div>
                <h1 className="text-3xl font-extrabold text-neutral-900">Reports</h1>
                <p className="mt-1 text-sm text-neutral-500">Pull revenue rollups by month, trend or district.</p>
            </header>
            <Tabs defaultValue="monthly" className="w-full">
                <TabsList className="grid w-full grid-cols-3 md:w-auto md:inline-flex" data-testid="reports-tabs">
                    <TabsTrigger value="monthly" data-testid="reports-tab-monthly">Monthly</TabsTrigger>
                    <TabsTrigger value="summary" data-testid="reports-tab-summary">Trend (N months)</TabsTrigger>
                    <TabsTrigger value="district" data-testid="reports-tab-district">By district</TabsTrigger>
                </TabsList>
                <TabsContent value="monthly"><MonthlyReport /></TabsContent>
                <TabsContent value="summary"><SummaryReport /></TabsContent>
                <TabsContent value="district"><DistrictReport /></TabsContent>
            </Tabs>
        </div>
    );
}

function MonthlyReport() {
    const [y, setY] = useState(nowY);
    const [m, setM] = useState(nowM);
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/admin/reports/monthly-revenue?year=${y}&month=${m}`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load monthly report"));
    }, [y, m]);

    const downloadCsv = () => {
        if (!data) return;
        const lines = [["date", "total_paise", "subscription_paise", "other_paise"].join(",")];
        for (const d of data.daily_series) {
            lines.push([d.date, d.total_paise, d.subscription_paise, d.other_paise].join(","));
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `revenue-${y}-${String(m).padStart(2, "0")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="mt-4 space-y-4" data-testid="monthly-report">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Select value={String(y)} onValueChange={(v) => setY(Number(v))}>
                        <SelectTrigger className="w-28" data-testid="report-year"><SelectValue /></SelectTrigger>
                        <SelectContent>{[nowY, nowY - 1, nowY - 2].map((yr) => <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={String(m)} onValueChange={(v) => setM(Number(v))}>
                        <SelectTrigger className="w-36" data-testid="report-month"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => (
                                <SelectItem key={mm} value={String(mm)}>{new Date(2000, mm - 1, 1).toLocaleString("en-IN", { month: "long" })}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={downloadCsv} disabled={!data} data-testid="report-csv-btn">
                        <Download size={14} className="mr-2" /> CSV
                    </Button>
                    <Button variant="outline" onClick={() => window.print()} data-testid="report-print-btn">
                        <Printer size={14} className="mr-2" /> Print / PDF
                    </Button>
                </div>
            </div>

            {!data ? (
                <Skeleton className="h-96 w-full rounded-2xl" />
            ) : (
                <>
                    <Card className="rounded-2xl border border-neutral-200 p-4">
                        <div className="text-xs uppercase text-neutral-500">Total revenue</div>
                        <div className="mt-1 text-4xl font-extrabold text-orange-600" data-testid="report-total-value">{formatInr(data.total_revenue_paise)}</div>
                    </Card>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <Card className="rounded-2xl border border-neutral-200 p-4">
                            <h3 className="text-sm font-bold">Daily series</h3>
                            <div className="h-64">
                                <ResponsiveContainer>
                                    <LineChart data={data.daily_series.map((d) => ({ ...d, total: d.total_paise / 100, sub: d.subscription_paise / 100 }))}>
                                        <CartesianGrid stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(8)} stroke="#94a3b8" />
                                        <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                                        <Tooltip formatter={(v) => formatInr(Number(v) * 100)} />
                                        <Line type="monotone" dataKey="total" stroke="#FF7A1A" strokeWidth={2} dot={false} name="Total" />
                                        <Line type="monotone" dataKey="sub" stroke="#FFC300" strokeWidth={2} dot={false} name="Subs" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                        <Card className="rounded-2xl border border-neutral-200 p-4">
                            <h3 className="text-sm font-bold">By transaction type</h3>
                            <div className="h-64">
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={TX_TYPES.map((t) => ({ name: t.label, value: (data.by_type?.[t.value] || 0) / 100 })).filter((r) => r.value > 0)} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                                            {TX_TYPES.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => formatInr(Number(v) * 100)} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </div>
                    <Card className="rounded-2xl border border-neutral-200 p-4">
                        <h3 className="text-sm font-bold">By district</h3>
                        <div className="h-64">
                            <ResponsiveContainer>
                                <BarChart data={data.by_district.map((d) => ({ name: d.district_name, revenue: d.total_paise / 100 }))} layout="vertical" margin={{ left: 32 }}>
                                    <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} stroke="#94a3b8" />
                                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#94a3b8" width={100} />
                                    <Tooltip formatter={(v) => formatInr(Number(v) * 100)} />
                                    <Bar dataKey="revenue" fill="#FF7A1A" radius={[0, 6, 6, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}

function SummaryReport() {
    const [n, setN] = useState(6);
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/admin/reports/revenue-summary?months=${n}`).then((r) => setData(r.data));
    }, [n]);
    const rows = (data?.series || []).map((r) => ({ label: monthNameOf(r.year, r.month), total: r.total_paise / 100 }));

    return (
        <div className="mt-4 space-y-4">
            <div className="flex items-center gap-2">
                <Label className="text-xs">Show last</Label>
                <Select value={String(n)} onValueChange={(v) => setN(Number(v))}>
                    <SelectTrigger className="w-24" data-testid="summary-months"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {[3, 6, 12].map((v) => <SelectItem key={v} value={String(v)}>{v} months</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => window.print()}><Printer size={14} className="mr-2" /> Print / PDF</Button>
            </div>
            <Card className="rounded-2xl border border-neutral-200 p-4">
                <div className="h-72">
                    <ResponsiveContainer>
                        <LineChart data={rows}>
                            <CartesianGrid stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip formatter={(v) => formatInr(Number(v) * 100)} />
                            <Line type="monotone" dataKey="total" stroke="#FF7A1A" strokeWidth={2.5} name="Total revenue" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </div>
    );
}

function DistrictReport() {
    const [start, setStart] = useState(new Date(nowY, nowM - 6, 1).toISOString().slice(0, 10));
    const [end, setEnd] = useState(todayIso());
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!start || !end) return;
        api.get(`/admin/reports/by-district?start=${start}&end=${end}`).then((r) => setData(r.data));
    }, [start, end]);

    return (
        <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <Label className="text-xs">Start</Label>
                    <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="district-start" />
                </div>
                <div>
                    <Label className="text-xs">End</Label>
                    <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="district-end" />
                </div>
                <Button variant="outline" onClick={() => window.print()}><Printer size={14} className="mr-2" /> Print / PDF</Button>
            </div>
            <Card className="rounded-2xl border border-neutral-200 p-4">
                <div className="text-xs text-neutral-500">Total in window</div>
                <div className="text-3xl font-extrabold text-orange-600" data-testid="district-total">{formatInr(data?.total_paise)}</div>
                <div className="mt-4 h-96">
                    <ResponsiveContainer>
                        <BarChart data={(data?.rows || []).map((d) => ({ name: d.district_name, revenue: d.total_paise / 100 }))} layout="vertical" margin={{ left: 40 }}>
                            <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={120} />
                            <Tooltip formatter={(v) => formatInr(Number(v) * 100)} />
                            <Bar dataKey="revenue" fill="#FF7A1A" radius={[0, 6, 6, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </div>
    );
}
